import React, { useState, useEffect } from 'react';
import { Button, Card, CardContent, CardHeader, Typography, Box, CircularProgress, Chip } from "@mui/material";
import { TabPanel } from '@mui/lab';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { getInactiveClients } from "../redux/ducks/invoices";
import ContactClientDialog from "./ContactClientDialog";
import { formatDayShort } from "../utils/dateUtils";

const InactiveTabPanel = styled(TabPanel)(({ theme }) => ({
    padding: 0,
    paddingTop: theme.spacing(3),
}));

const CenteredLoading = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
}));

const ClientList = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
}));

const ClientRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'stretch',
        padding: theme.spacing(1.5),
    },
}));

const ClientInfo = styled(Box)({
    minWidth: 0,
    flex: '1 1 auto',
});

const MobileOnlyChipWrap = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(0.5),
    display: 'none',
    [theme.breakpoints.down('sm')]: {
        display: 'block',
    },
}));

const DesktopOnlyChip = styled(Chip)(({ theme }) => ({
    [theme.breakpoints.down('sm')]: {
        display: 'none',
    },
}));

const ClientActions = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    flexShrink: 0,
    [theme.breakpoints.down('sm')]: {
        '& > *': {
            flex: '1 1 0',
        },
    },
}));

const EmptyMessage = styled(Typography)(({ theme }) => ({
    padding: theme.spacing(2),
    textAlign: 'center',
}));

const formatDate = (d) => formatDayShort(d);

const daysSince = (d) => {
    if (!d) return null;
    const ms = Date.now() - new Date(d).getTime();
    return Math.floor(ms / (1000 * 60 * 60 * 24));
};

const BillingInactiveClients = ({ tabValue }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(false);
    const [contactTarget, setContactTarget] = useState(null);

    useEffect(() => {
        if (tabValue === 'inactive') {
            loadClients();
        }
    }, [tabValue]);

    const loadClients = () => {
        setLoading(true);
        dispatch(getInactiveClients((data) => {
            setLoading(false);
            setClients(Array.isArray(data) ? data : []);
        }));
    };

    return (
        <InactiveTabPanel value="inactive">
            <Card>
                <CardHeader
                    title="Inactive Clients"
                    subheader="Clients with work in the last 12 months but nothing in the last 2 months"
                />
                <CardContent>
                    {loading ? (
                        <CenteredLoading>
                            <CircularProgress />
                        </CenteredLoading>
                    ) : clients.length > 0 ? (
                        <ClientList>
                            {clients.map((client) => {
                                const days = daysSince(client.lastProjectDate);
                                return (
                                    <ClientRow key={client._id}>
                                        <ClientInfo>
                                            <Typography variant="body1" fontWeight="medium">
                                                {client.name}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Last project: {client.lastProjectName} ({formatDate(client.lastProjectDate)})
                                                {client.lastProjectAccountName && ` — ${client.lastProjectAccountName}`}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {client.projectCount} project{client.projectCount === 1 ? '' : 's'} in last 12 months
                                            </Typography>
                                            {days !== null && (
                                                <MobileOnlyChipWrap>
                                                    <Chip
                                                        size="small"
                                                        label={`${days}d inactive`}
                                                        color={days > 180 ? 'error' : days > 90 ? 'warning' : 'default'}
                                                    />
                                                </MobileOnlyChipWrap>
                                            )}
                                        </ClientInfo>
                                        <ClientActions>
                                            {days !== null && (
                                                <DesktopOnlyChip
                                                    size="small"
                                                    label={`${days}d inactive`}
                                                    color={days > 180 ? 'error' : days > 90 ? 'warning' : 'default'}
                                                />
                                            )}
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={() => navigate(`/billing/client/${client._id}`)}
                                            >
                                                View
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={() => setContactTarget(client)}
                                            >
                                                Check-In
                                            </Button>
                                        </ClientActions>
                                    </ClientRow>
                                );
                            })}
                        </ClientList>
                    ) : (
                        <EmptyMessage variant="body2" color="text.secondary">
                            No inactive clients
                        </EmptyMessage>
                    )}
                </CardContent>
            </Card>
            <ContactClientDialog
                open={Boolean(contactTarget)}
                client={contactTarget}
                onClose={() => setContactTarget(null)}
                defaultSubject="Checking in"
            />
        </InactiveTabPanel>
    );
};

export default BillingInactiveClients;
